import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';

import { TranslatePipe } from '@ngx-translate/core';
import { ShippingHandlingData } from '../../../../models/sol-estimate-table/ShippingHandlingData';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { ShippingHandlingFormGroup, ShippingHandlingRowFormGroup } from '../../../../models/sol-estimate-table/ShippingHandlingFormGroup';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { SolEstimateMaterialService } from '../../../../services/estimate/sol-estimate-material.service';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { SolMaterialEscalationType } from '../../../../models/enum';

import { NumericMaskDirective } from '../../../../shared/numeric/numeric-mask.directive';
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
@Component({
  selector: 'app-sol-shipping-and-handling-dialog',
  imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    TranslatePipe,
    MatButtonModule,
    MatTableModule,
    MatFormField,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NumericMaskDirective,
    MultilingualCurrencyPipe,
    CloseOnEscapeDirective
],
  templateUrl: './sol-shipping-and-handling-dialog.component.html',
  styleUrl: './sol-shipping-and-handling-dialog.component.scss'
})
export class SolShippingAndHandlingDialogComponent implements OnInit {

  shippingDataSource: ShippingHandlingData[];
  displayedColumns: string[] = ['shipping', 'dollars', 'rate', 'total'];
  shippingAndHandlingFormGroup! : FormGroup<ShippingHandlingFormGroup>;
  readonly formGroupNames = [
    CONSTANTS.WARRANTY_FORM_GROUP_SIEMENS,
    CONSTANTS.WARRANTY_FORM_GROUP_OUTSIDE,
    CONSTANTS.WARRANTY_FORM_GROUP_MECHANICAL,
    CONSTANTS.WARRANTY_FORM_GROUP_ELECTRICAL
  ];
  totalShippingCost: number = CONSTANTS.ZERO;

  /** Precision follows the app-wide decimal preference, same as the legacy directives. */
  readonly rateConfig: NumericFieldConfig = {
    kind: 'percentage',
    allowNegative: false,
    max: CONSTANTS.SHIPPING_RATE_MAX_VALUE,
    useGrouping: false,
    step: CONSTANTS.ZERO_POINT_ONE,
    revertToLastValidValue: true
  };

  readonly sellPriceConfig: NumericFieldConfig = {
    kind: 'currency',
    allowNegative: false,
    max: CONSTANTS.SHIPPING_SELL_PRICE_MAX_VALUE,
    useGrouping: true,
    showStepper: false,
    step: CONSTANTS.ONE,
    revertToLastValidValue: true
  };
  constructor(
    public dialogRef: MatDialogRef<SolShippingAndHandlingDialogComponent>,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly materialService: SolEstimateMaterialService
    ) { }

  ngOnInit(): void {
    this.shippingDataSource = structuredClone(this.materialService.getShippingData());
    this.initializeFormGroup();
    this.subscribeToShippingHandlingChanges();
  }

  private initializeFormGroup(): void {
    this.shippingAndHandlingFormGroup = this.formBuilder.group<ShippingHandlingFormGroup>({
      siemensMaterialGroup : this.patchShippingHandlingRowFormGroupValues(CONSTANTS.ZERO),
      outsideMaterialGroup : this.patchShippingHandlingRowFormGroupValues(CONSTANTS.ONE),
      mechanicalMaterialGroup : this.patchShippingHandlingRowFormGroupValues(CONSTANTS.TWO),
      electricalMaterialGroup : this.patchShippingHandlingRowFormGroupValues(CONSTANTS.THREE)
    });

    this.updateTotalShippingCost();
  }
  private subscribeToShippingHandlingChanges(): void {
    this.formGroupNames.forEach((_groupName, index) => {
      const group = this.getRowGroup(index);

      group.controls.rate.valueChanges.subscribe(() => {
        this.onRateChange(index);
      });
      group.controls.total.valueChanges.subscribe(() => {
        this.onTotalChange(index);
      });
    });
  }

  private patchShippingHandlingRowFormGroupValues(index: number): FormGroup<ShippingHandlingRowFormGroup> {
    const shippingData = this.shippingDataSource[index];
    const dollars = shippingData.dollars || CONSTANTS.ZERO;
    const rate = shippingData.rate || CONSTANTS.ZERO;
    const total = this.materialService.calculateShippingCost(dollars, rate, this.getMaterialCategory(index));
    this.shippingDataSource[index].total = total;

    const totalControl = this.formBuilder.control<number>(total);
    if (total === CONSTANTS.ZERO) {
      totalControl.disable({ emitEvent: false });
    }

    return this.formBuilder.group<ShippingHandlingRowFormGroup>({
      shipping: this.formBuilder.control<string>(shippingData.shipping),
      dollars: this.formBuilder.control<number>(dollars),
      rate: this.formBuilder.control<number>(rate),
      total: totalControl
    });
  }

  private updateTotalShippingCost(): void {
    this.totalShippingCost = this.formGroupNames.reduce((sum, _groupName, index) => {
      const group = this.getRowGroup(index);
      return sum + (group.controls.total.value || CONSTANTS.ZERO);
    }, CONSTANTS.ZERO);
  }

  onSave(): void {
    if (this.shippingAndHandlingFormGroup.valid) {
      this.dialogRef.close(this.shippingDataSource);
    }
  }

  getFormGroupName(index: number): string {
    return this.formGroupNames[index];
  }

  private getRowGroup(index: number): FormGroup<ShippingHandlingRowFormGroup> {
    return this.shippingAndHandlingFormGroup.get(this.formGroupNames[index]) as FormGroup<ShippingHandlingRowFormGroup>;
  }

  private getMaterialCategory(index: number): SolMaterialEscalationType {
    return Object.values(SolMaterialEscalationType)[index];
  }

  onRateChange(index: number): void {
    const group = this.getRowGroup(index);
    const rate = group.controls.rate.value || CONSTANTS.ZERO;
    const dollars = group.controls.dollars.value || CONSTANTS.ZERO;
    const totalControl = group.controls.total;

    const newTotal = this.materialService.calculateShippingCost(dollars, rate, this.getMaterialCategory(index));
    totalControl.setValue(newTotal, {emitEvent: false});
    this.updateTotalControlState(totalControl, dollars);
    this.shippingDataSource[index].total = newTotal;
    this.shippingDataSource[index].rate = rate;
    this.updateTotalShippingCost();
  }

  onTotalChange(index: number): void {
    const group = this.getRowGroup(index);
    const dollars = group.controls.dollars.value || CONSTANTS.ZERO;
    const total = group.controls.total.value || CONSTANTS.ZERO;
    const totalControl = group.controls.total;

    const newRate = this.materialService.calculateShippingRateFromTotal(total, dollars);
    group.controls.rate.setValue(newRate, {emitEvent: false});
    this.shippingDataSource[index].rate = newRate;
    this.shippingDataSource[index].total = total;
    this.updateTotalControlState(totalControl, dollars);
    this.updateTotalShippingCost();
  }

  private updateTotalControlState(totalControl: FormGroup<ShippingHandlingRowFormGroup>['controls']['total'], totalValue: number): void {
    if (totalValue === CONSTANTS.ZERO) {
      totalControl.disable({ emitEvent: false });
      return;
    }
    totalControl.enable({ emitEvent: false });
  }

  /** TrackBy function for mat-table rows. */
  trackByIndex(index: number): number {
    return index;
  }

}
